import React, { useState, useEffect, useRef } from "react";
import "../styles/TwoFA.css";
import { FaChevronLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import HeaderLogo from "../components/HeaderLogo";
import { API_URL } from "../constant";

function TwoFAVerificationPage() {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [retryAfter, setRetryAfter] = useState(null);
  const inputsRef = useRef([]);

  const userId = localStorage.getItem('superadmin_user_id');
  const userEmail = localStorage.getItem('superadmin_email');

  // Countdown for OTP timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  // Countdown for retry after and update error message dynamically
  useEffect(() => {
    if (retryAfter > 0) {
      const timer = setInterval(() => {
        setRetryAfter((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setError(""); // Clear error when timer expires
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [retryAfter]);

  // Update error message when retryAfter changes
  useEffect(() => {
    if (retryAfter > 0) {
      const minutes = Math.ceil(retryAfter / 60);
      setError(`Too many failed attempts. Please try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`);
    }
  }, [retryAfter]);

  const handleResend = async () => {
    if (canResend && !isResending && retryAfter === 0) {
      setIsResending(true);
      
      try {
        const response = await fetch(`${API_URL}/superadmin/resend-login-otp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            email: userEmail,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          console.log("OTP resent successfully");
          setTimeLeft(60);
          setCanResend(false);
          setError("");
          setRemainingAttempts(data.remaining_attempts || null);
        } else {
          if (data.retry_after_seconds) {
            setRetryAfter(data.retry_after_seconds);
          } else {
            setError(data.message || 'Failed to resend OTP');
          }
        }
      } catch (error) {
        console.error('Resend OTP error:', error);
        setError('Network error. Please try again.');
      } finally {
        setIsResending(false);
      }
    }
  };

  const handleChange = (value, index) => {
    if (/^[0-9]$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setError("");

      if (index < otp.length - 1) {
        inputsRef.current[index + 1].focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split("");
      setOtp(newOtp);
      setError("");
      inputsRef.current[otp.length - 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index] === "") {
        if (index > 0) inputsRef.current[index - 1].focus();
      } else {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }
  };

  const handleVerify = async () => {
    const enteredOtp = otp.join("");
    
    if (enteredOtp.length !== 6) {
      setError("Please enter all 6 digits");
      return;
    }

    if (!userId) {
      setError("Session expired. Please login again.");
      setTimeout(() => navigate("/"), 2000);
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/superadmin/verify-superadmin-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          user_id: parseInt(userId),
          otp: enteredOtp,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store the authentication token
        localStorage.setItem('auth_token', data.access_token);
        
        // Clear temporary storage
        localStorage.removeItem('superadmin_user_id');
        localStorage.removeItem('superadmin_email');
        
        console.log("OTP Verified Successfully!");
         navigate("/dashboard", { state: { message: "Login successfully!" } });
      } else {
        let errorMessage = data.message || "Invalid OTP. Please try again.";
        
        // Add remaining attempts to error message if available
        if (data.remaining_attempts !== undefined) {
          errorMessage += ` (${data.remaining_attempts} attempts remaining)`;
          setRemainingAttempts(data.remaining_attempts);
        }
        
        // Handle rate limiting
        if (data.retry_after_seconds) {
          setRetryAfter(data.retry_after_seconds);
          // The useEffect will handle updating the error message dynamically
        } else {
          setError(errorMessage);
        }
        
        setOtp(new Array(6).fill(""));
        setTimeout(() => {
          inputsRef.current[0]?.focus();
        }, 50);
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    // Clear temporary storage when going back
    localStorage.removeItem('superadmin_user_id');
    localStorage.removeItem('superadmin_email');
    navigate("/");
  };

  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  return (
    <div className="twofa-container">
      <HeaderLogo/>

      <div className="twofa-card">
        <div className="back" onClick={handleBack}>
          <FaChevronLeft className="back-icon" />
          <span className="back-text">Back</span>
        </div>

        <h2 className="twofa-heading">2FA Verification</h2>
        <p className="twofa-subtext">
          Please enter the 2FA code sent to your registered email address to complete your Verification
        </p>

        <div className="twofa-otp-boxes">
          {otp.map((digit, i) => (
            <input
              key={i}
              type="text"
              maxLength={255}
              value={digit}
              ref={(el) => (inputsRef.current[i] = el)}
              onChange={(e) => handleChange(e.target.value, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              onPaste={handlePaste}
              className="twofa-otp-input"
              disabled={isLoading || retryAfter > 0}
            />
          ))}
          <p className="twofa-error">
            {error || " "}
          </p>
        </div>

        <div className="twofa-resend">
          Didn't get the code?
          <button
            onClick={handleResend}
            disabled={!canResend || isResending || retryAfter > 0}
            className={`twofa-resend-btn ${canResend && !isResending && retryAfter === 0 ? "active" : "disabled"}`}
          >
            {isResending ? "Sending..." : "Resend"}
          </button>
        </div>

        {!error.includes("Too many failed attempts") && (
          <div className={`twofa-timer-wrapper ${canResend ? "fade-out" : ""}`}>
            {!canResend && (
              <p className="twofa-timer">
                Time remaining:{" "}
                <span>
                  {formatTime(timeLeft)}
                </span>
              </p>
            )}
          </div>
        )}

        <button
          className="twofa-verify-btn"
          onClick={handleVerify}
          disabled={isLoading || otp.join("").length !== 6 || retryAfter > 0}
        >
          {isLoading ? "Verifying..." : "Verify"}
        </button>
      </div>
    </div>
  );
}

export default TwoFAVerificationPage;
